import honeybadger from "@honeybadger-io/js";
import axios from "axios";
import { GatewayTimeoutError } from "backend/fetcher";
import { getServerInfo } from "./server";
import { buildVersion } from "./version";

let instance: honeybadger;

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- eslintintroduction
const delayedContext: any[] = [];

interface HoneybadgerFrame {
    file?: string;
    function?: string;
    method?: string;
    number?: number;
}

interface HoneybadgerNotice {
    name?: string;
    message?: string;
    stack?: string;
    backtrace?: HoneybadgerFrame[];
}

const THIRD_PARTY_DOMAINS = ["helpscout.net"];

const isNonAppFrame = (frame: HoneybadgerFrame): boolean =>
    frame?.file === "[native code]" ||
    frame?.file?.includes("<unknown>") ||
    !!frame?.file?.match(/<anonymous.*>/);

const isEntirelyFromThirdPartyDomains = (notice: HoneybadgerNotice): boolean => {
    if (!notice?.backtrace?.length) {
        return false;
    }

    const hasThirdPartyFrame = notice.backtrace.some(frame =>
        THIRD_PARTY_DOMAINS.some(domain => frame?.file?.includes(domain)),
    );
    if (!hasThirdPartyFrame) {
        return false;
    }

    return notice.backtrace.every(
        frame =>
            THIRD_PARTY_DOMAINS.some(domain => frame?.file?.includes(domain)) ||
            isNonAppFrame(frame),
    );
};

const isThirdPartyChunkLoadError = (notice: HoneybadgerNotice): boolean => {
    if (notice?.name !== "ChunkLoadError") {
        return false;
    }
    return THIRD_PARTY_DOMAINS.some(domain => notice.message?.includes(domain));
};

const isReactPdfRaceCondition = (notice: HoneybadgerNotice): boolean => {
    if (!notice?.message?.includes("getComputedStyle")) {
        return false;
    }
    if (notice?.stack?.includes("pdf")) {
        return true;
    }
    return notice?.backtrace?.some(frame => frame?.file?.includes("pdf")) ?? false;
};

export const isMaplibreInternalError = (notice: HoneybadgerNotice): boolean => {
    if (notice?.stack?.includes("maplibre")) {
        return true;
    }
    if (notice?.backtrace?.some(frame => frame?.file?.includes("maplibre"))) {
        return true;
    }
    const maplibreMessagePatterns = [
        "is not iterable",
        "transient activation",
        "doesn't match current length",
    ];
    const isMaplibreMessage = maplibreMessagePatterns.some(pattern =>
        notice?.message?.includes(pattern),
    );
    const isMapChunkOrNativeFrame = (frame: HoneybadgerFrame) =>
        frame?.file?.includes("/map-") || frame?.file === "[native code]";
    const allFramesAreMapChunk =
        notice?.backtrace?.length > 0 && notice.backtrace.every(isMapChunkOrNativeFrame);
    return isMaplibreMessage && allFramesAreMapChunk;
};

export const isExternalError = (notice: HoneybadgerNotice): boolean => {
    if (notice?.stack && /chrome-extension:\/\//.test(notice.stack)) {
        return true;
    }

    if (!notice?.backtrace?.length) {
        return false;
    }

    const honeybadgerFiles = new Set(
        notice.backtrace
            .filter(frame => frame?.method?.includes("___hb"))
            .map(frame => frame?.file)
            .filter(Boolean),
    );

    const allFramesAreUnknown = notice.backtrace.every(
        (frame: HoneybadgerFrame) =>
            frame?.file?.includes("chrome-extension://") ||
            frame?.file?.includes("<unknown>") ||
            frame?.file?.match(/<anonymous.*>/) ||
            frame?.file === "[native code]" ||
            honeybadgerFiles.has(frame?.file),
    );

    if (allFramesAreUnknown) {
        return true;
    }

    return isEntirelyFromThirdPartyDomains(notice);
};

export const shouldFilterDynamicImportError = (notice: HoneybadgerNotice): boolean => {
    if (!notice?.message) {
        return false;
    }

    if (notice.message.includes("Failed to fetch dynamically imported module")) {
        return true;
    }

    const isPdfWorkerLoadError =
        notice.message.includes("error loading dynamically imported module") &&
        (notice.message.includes("pdf.worker") || notice.message.includes("pdfjs-dist"));

    return isPdfWorkerLoadError;
};

export const isFramerMotionScrollError = (notice: HoneybadgerNotice): boolean => {
    if (!notice?.message || !notice?.stack) {
        return false;
    }

    const isScrollLeftNullError =
        notice.message.includes("Cannot read properties of null") &&
        notice.message.includes("scrollLeft");

    if (!isScrollLeftNullError) {
        return false;
    }

    return notice.stack.includes("measureScroll");
};

export const isEmptyObjectRejectionWithNoUsefulTrace = (
    notice: HoneybadgerNotice,
): boolean => {
    if (
        notice?.message !== "UnhandledPromiseRejectionWarning: {}" &&
        notice?.message !== "UnhandledPromiseRejectionWarning: Unspecified reason" &&
        notice?.message !== "UnhandledPromiseRejectionWarning: undefined"
    ) {
        return false;
    }

    if (!notice?.backtrace?.length) {
        return true;
    }

    return notice.backtrace.every((frame, _, arr) => {
        const methodName = frame?.method || frame?.function;
        const isMinifiedMethod =
            !methodName || methodName === "<unknown>" || methodName.length <= 2;
        const isSameLineAsOthers = arr.every(f => f.number === frame.number);
        return isMinifiedMethod && isSameLineAsOthers;
    });
};

const TWILIO_AUDIO_OUTPUT_FRAME_METHODS = [
    "_setSinkIds",
    "_updateSinkIds",
    "_removeLostOutput",
];

export const isTwilioAudioOutputSelectionError = (notice: HoneybadgerNotice): boolean => {
    if (notice?.name !== "NotSupportedError") {
        return false;
    }

    if (!notice?.message?.includes("Audio output selection is not supported")) {
        return false;
    }

    if (
        TWILIO_AUDIO_OUTPUT_FRAME_METHODS.some(method => notice?.stack?.includes(method))
    ) {
        return true;
    }

    return (
        notice?.backtrace?.some(frame =>
            TWILIO_AUDIO_OUTPUT_FRAME_METHODS.some(
                method =>
                    frame?.method?.includes(method) || frame?.function?.includes(method),
            ),
        ) ?? false
    );
};

const NOTABLE_ABORT_ERROR_MESSAGES = ["signal timed out"];

export const isBenignAbortError = (notice: HoneybadgerNotice): boolean => {
    if (notice?.name !== "AbortError") {
        return false;
    }

    return !NOTABLE_ABORT_ERROR_MESSAGES.some(msg => notice?.message?.includes(msg));
};

export const isWebkitMaskedUrlError = (notice: HoneybadgerNotice): boolean => {
    if (!notice?.stack?.includes("webkit-masked-url://hidden/")) {
        return false;
    }

    const unspecifiedReasonMessages = [
        "UnhandledPromiseRejectionWarning: Unspecified reason",
        "UnhandledPromiseRejectionWarning: {}",
        "UnhandledPromiseRejectionWarning: undefined",
    ];

    return unspecifiedReasonMessages.some(msg => notice?.message === msg);
};

export const isMapboxGlError = (notice: HoneybadgerNotice): boolean => {
    if (!notice?.message) {
        return false;
    }

    const mapboxPatterns = [
        "opacityVertexArray.length",
        "layoutVertexArray.length",
        "Could not compile fragment shader",
        "Could not compile vertex shader",
        "Program failed to link",
    ];

    return mapboxPatterns.some(pattern => notice.message?.includes(pattern));
};

const FAILED_FETCH_MESSAGES = ["Failed to fetch", "Load failed"];

export const isFailedToFetchWithNoUsefulTrace = (notice: HoneybadgerNotice): boolean => {
    if (!FAILED_FETCH_MESSAGES.includes(notice?.message)) {
        return false;
    }

    if (!notice?.backtrace?.length) {
        return true;
    }

    const isHoneybadgerInternal = notice.backtrace.some(
        frame =>
            frame?.method === "Object.postUserData" ||
            frame?.method === "postUserData" ||
            frame?.method?.includes("___hb"),
    );
    if (isHoneybadgerInternal) {
        return true;
    }

    const allFramesLackUsefulInfo = notice.backtrace.every(frame => {
        const method = frame?.method || frame?.function;
        return !method || method === "<unknown>";
    });
    if (allFramesLackUsefulInfo) {
        return true;
    }

    return false;
};

const isFromReactDomOrVendorBundle = (notice: HoneybadgerNotice): boolean => {
    if (
        notice.stack?.includes("react-dom") ||
        notice.stack?.includes("react-dom.production")
    ) {
        return true;
    }

    if (notice?.backtrace?.some(frame => frame?.file?.includes("/vendor-"))) {
        return true;
    }

    return false;
};

export const isDomManipulationErrorFromExtension = (
    notice: HoneybadgerNotice,
): boolean => {
    if (!notice?.message) {
        return false;
    }

    const domManipulationPatterns = [
        "Failed to execute 'removeChild' on 'Node'",
        "Failed to execute 'insertBefore' on 'Node'",
        "Failed to execute 'appendChild' on 'Node'",
        "The node to be removed is not a child of this node",
        "The object can not be found here.",
    ];

    if (!isFromReactDomOrVendorBundle(notice)) {
        return false;
    }

    return domManipulationPatterns.some(pattern => notice.message?.includes(pattern));
};

export const isCircularJsonSerializationErrorFromExtension = (
    notice: HoneybadgerNotice,
): boolean => {
    if (!notice?.message?.includes("Converting circular structure to JSON")) {
        return false;
    }

    return (
        notice.message.includes("__reactFiber$") ||
        notice.message.includes("__reactProps$")
    );
};

export const isLikelyCefSharpBotError = (notice: HoneybadgerNotice): boolean => {
    return !!notice?.message?.match(/Object Not Found Matching Id:\d+, MethodName:\w+/);
};

const INJECTED_BROWSER_GLOBALS = ["__firefox__", "__gCrWeb"];

export const isInjectedBrowserGlobalError = (notice: HoneybadgerNotice): boolean => {
    if (notice?.name !== "ReferenceError") {
        return false;
    }
    return INJECTED_BROWSER_GLOBALS.some(
        identifier =>
            notice?.message?.includes(`Can't find variable: ${identifier}`) ||
            notice?.message?.includes(`${identifier} is not defined`),
    );
};

const GLOBAL_SCOPE_METHOD = "global code";

const isJavascriptAsset = (file: string): boolean => {
    const [path] = file.split(/[?#]/);
    return path.endsWith(".js");
};

export const isInlineGlobalScopeError = (notice: HoneybadgerNotice): boolean => {
    if (notice?.backtrace?.length !== 1) {
        return false;
    }

    const [frame] = notice.backtrace;
    if (frame?.method !== GLOBAL_SCOPE_METHOD || !frame.file) {
        return false;
    }

    return !isJavascriptAsset(frame.file);
};

export const isPosthogSessionRecordingError = (notice: HoneybadgerNotice): boolean => {
    if (
        notice?.message?.includes(
            "Called on script loaded before session recording is available",
        )
    ) {
        return true;
    }

    const frames = notice?.backtrace;
    if (Array.isArray(frames) && frames.length > 0) {
        const allPosthog = frames.every(
            frame =>
                frame?.file?.includes("posthog.com/") || frame?.file === "<anonymous>",
        );
        if (allPosthog) {
            return true;
        }
    }

    return false;
};

export const isGatewayTimeoutError = (error: unknown): boolean => {
    return error instanceof GatewayTimeoutError;
};

export const isUnauthenticatedError = (error: unknown): boolean =>
    axios.isAxiosError(error) && error.response?.status === 401;

export const isUnauthorizedError = (error: unknown): boolean =>
    axios.isAxiosError(error) && error.response?.status === 403;

export const shouldSkipReporting = (error: unknown): boolean => {
    if (!error || typeof error !== "object") {
        return false;
    }

    const record = error as Record<string, unknown>;
    if (record.skipReporting) {
        return true;
    }

    const cause = record.cause;
    if (cause && typeof cause === "object") {
        return !!(cause as Record<string, unknown>).skipReporting;
    }

    return false;
};

export const safeStringify = (value: unknown): string | undefined => {
    try {
        return JSON.stringify(value);
    } catch {
        return undefined;
    }
};

const setupUnhandledRejectionHandler = () => {
    window.addEventListener("unhandledrejection", (event: PromiseRejectionEvent) => {
        if (!instance) {
            return;
        }

        const reason = event.reason;

        if (shouldSkipReporting(reason)) {
            return;
        }

        if (isUnauthorizedError(reason)) {
            return;
        }

        if (reason instanceof Error) {
            const stack = reason.stack || `${reason.message}\n    at ? (unknown:0)`;
            const err = {
                name: reason.name,
                message: `UnhandledPromiseRejectionWarning: ${reason}`,
                stack: stack,
            };

            instance.addBreadcrumb(`window.onunhandledrejection: ${err.name}`, {
                category: "error",
                metadata: err,
            });

            instance.notify(reason);
            return;
        }

        const stringified = safeStringify(reason);
        const message =
            typeof reason === "string"
                ? reason
                : stringified && stringified !== "{}"
                  ? stringified
                  : "Unspecified reason";

        instance.addBreadcrumb(`window.onunhandledrejection: ${message}`, {
            category: "error",
            metadata: { reason },
        });

        instance.notify({
            name: "UnhandledPromiseRejection",
            message: `UnhandledPromiseRejectionWarning: ${message}`,
        });
    });
};

export const configureErrorTracking = async () => {
    const serverInfo = await getServerInfo();
    if (!instance) {
        honeybadger.beforeNotify((notice): boolean | void => {
            if (
                notice?.name === "ClientGraphqlNetworkError" ||
                notice?.name === "ClientHttpNetworkError" ||
                notice?.name === "UnauthenticatedError"
            ) {
                return false;
            }

            if (notice?.message?.includes("ResizeObserver")) {
                return false;
            }

            if (isFramerMotionScrollError(notice)) {
                return false;
            }

            if (shouldFilterDynamicImportError(notice)) {
                return false;
            }

            if (
                notice?.message?.includes("Unexpected keyword 'import'") &&
                (notice?.backtrace?.some(frame => frame?.file?.includes("<unknown>")) ||
                    notice?.stack?.includes("blob:"))
            ) {
                return false;
            }

            if (isMapboxGlError(notice)) {
                return false;
            }

            if (isMaplibreInternalError(notice)) {
                return false;
            }

            if (isFailedToFetchWithNoUsefulTrace(notice)) {
                return false;
            }

            if (isLikelyCefSharpBotError(notice)) {
                return false;
            }

            // Filter MetaMask browser extension errors
            if (notice?.message?.includes("Failed to connect to MetaMask")) {
                return false;
            }

            if (isWebkitMaskedUrlError(notice)) {
                return false;
            }

            if (isExternalError(notice)) {
                return false;
            }

            if (isInjectedBrowserGlobalError(notice)) {
                return false;
            }

            if (isInlineGlobalScopeError(notice)) {
                return false;
            }

            if (isThirdPartyChunkLoadError(notice)) {
                return false;
            }

            if (isReactPdfRaceCondition(notice)) {
                return false;
            }

            if (isEmptyObjectRejectionWithNoUsefulTrace(notice)) {
                return false;
            }

            if (isDomManipulationErrorFromExtension(notice)) {
                return false;
            }

            if (isCircularJsonSerializationErrorFromExtension(notice)) {
                return false;
            }

            if (isBenignAbortError(notice)) {
                return false;
            }

            if (isPosthogSessionRecordingError(notice)) {
                return false;
            }

            if (isTwilioAudioOutputSelectionError(notice)) {
                return false;
            }
        });
        instance = honeybadger.configure({
            apiKey: "6502fcd2",
            environment: serverInfo.environment,
            revision: buildVersion,
            enableUnhandledRejection: false,
        });

        instance.setContext({
            serverVersion: serverInfo.build,
        });

        setupUnhandledRejectionHandler();
    }

    delayedContext.forEach(x => x(instance));
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- eslintintroduction
(window as any).customReport = () => {
    try {
        throw "this is a test!";
    } catch (e) {
        honeybadger.notify(e instanceof Error ? e : new Error(String(e)));
    }
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- eslintintroduction
(window as any).customReportOnInstance = () => {
    try {
        throw "this is a test!";
    } catch (e) {
        instance.notify(e instanceof Error ? e : new Error(String(e)));
    }
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- eslintintroduction
(window as any).testFingerprint = () => {
    reportError(new Error("Test error with fingerprint"), {
        fingerprint: "test-fingerprint-123",
    });
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- eslintintroduction
(window as any).testParams = () => {
    reportError(new Error("Test error with params"), {
        params: { userId: "test-user-456", action: "button-click", count: 42 },
    });
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- eslintintroduction
(window as any).testContext = () => {
    reportError(new Error("Test error with context"), {
        context: { feature: "error-tracking-test", environment: "manual-test" },
    });
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- eslintintroduction
(window as any).testAllOptions = () => {
    reportError(new Error("Test error with all options"), {
        fingerprint: "combined-test-fingerprint",
        params: { source: "manual-test", timestamp: Date.now() },
        context: { testType: "full-options-test" },
    });
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- eslintintroduction
(window as any).testDuplicateFingerprint = () => {
    reportError(new Error("First error - should dedupe"), {
        fingerprint: "dedupe-test-fingerprint",
    });
    reportError(new Error("Second error - same fingerprint"), {
        fingerprint: "dedupe-test-fingerprint",
    });
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- eslintintroduction
export const setErrorTrackingContext = (context: any) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- eslintintroduction
    const callback = (i: any) => i.setContext(context);
    if (instance?.setContext) {
        callback(instance);
    } else {
        delayedContext.push(callback);
    }
};

const errorClassName = (error: unknown): string => {
    if (error && typeof error === "object") {
        const name = (error as { name?: unknown }).name;
        if (typeof name === "string" && name.length > 0) {
            return name;
        }
    }
    return "UnknownError";
};

export const makeErrorFingerprint = (
    scope: "query" | "mutation",
    key: string,
    error: unknown,
): string => `${scope}:${key}:${errorClassName(error)}`;

export interface ReportErrorOptions {
    fingerprint?: string;
    params?: Record<string, unknown>;
    context?: Record<string, unknown>;
}

export const reportError = (e: Error, options: ReportErrorOptions = {}) => {
    if (!instance) return;
    const notifyOptions: Record<string, unknown> = {};
    if (options.fingerprint) {
        notifyOptions.fingerprint = options.fingerprint;
    }
    if (options.params) {
        notifyOptions.params = options.params;
    }
    if (options.context) {
        notifyOptions.context = options.context;
    }
    instance.notify(e, Object.keys(notifyOptions).length > 0 ? notifyOptions : undefined);
};
